import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getBrandsApi, 
  getSlotsApi, 
  getCohortsApi, 
  createRequestApi,
  updateRequestApi,
  getAvailableSlotsApi,
  getCalendarBookingsApi
} from '../../utils/api';
import { formatNumber, getContrastColor } from '../../utils/helpers';
import CreativeUpload from '../../components/CreativeUpload';
import CampaignPreview from '../../components/CampaignPreview';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const RequestCampaign = ({ showToast, onNavigate, selectedBrand, isViewOnly }) => {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  
  // Check if we're editing (data from localStorage)
  const [editRequest, setEditRequest] = useState(null);
  
  useEffect(() => {
    const storedRequest = localStorage.getItem('editRequest');
    if (storedRequest) {
      setEditRequest(JSON.parse(storedRequest));
      localStorage.removeItem('editRequest'); // Clear after loading
    }
  }, []);

  const [brand, setBrand] = useState(null);
  const [brands, setBrands] = useState([]);
  const [slots, setSlots] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state - initialize with edit data if available
  const [campaignName, setCampaignName] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [cohortDefinition, setCohortDefinition] = useState('');
  const [expectedAudienceSize, setExpectedAudienceSize] = useState('');
  const [creative, setCreative] = useState(null);
  const [driveLink, setDriveLink] = useState('');
  const [contentText, setContentText] = useState('');
  const [contentCta, setContentCta] = useState('');
  const [note, setNote] = useState('');
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderEmailError, setReminderEmailError] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [calendarBookings, setCalendarBookings] = useState({}); // New state for calendar bookings
  
  // A/B Testing states
  const [abTestEnabled, setAbTestEnabled] = useState(false);
  const [activeVariation, setActiveVariation] = useState(1); // 1 or 2
  const [audienceSplit, setAudienceSplit] = useState({ variation1: 50, variation2: 50 });
  const [variation1, setVariation1] = useState({
    creative: null,
    driveLink: '',
    contentText: '',
    contentCta: ''
  });
  const [variation2, setVariation2] = useState({
    creative: null,
    driveLink: '',
    contentText: '',
    contentCta: ''
  });
  
  // New cohort creation states
  const [newCohortName, setNewCohortName] = useState('');
  const [newCohortCsv, setNewCohortCsv] = useState(null);
  const [creatingCohort, setCreatingCohort] = useState(false);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Cohort input type state
  const [cohortInputType, setCohortInputType] = useState('existing');
  const [csvFile, setCsvFile] = useState(null);
  const [manualCohortText, setManualCohortText] = useState('');

  // Update form fields when editRequest is loaded
  useEffect(() => {
    if (editRequest) {
      setCampaignName(editRequest.campaign_name || '');
      setSelectedSlot(editRequest.slot_id || null);
      setSelectedDate(editRequest.date || '');
      setSelectedCohort(editRequest.cohort_id || null);
      setCohortDefinition(editRequest.cohort_definition || '');
      setExpectedAudienceSize(editRequest.expected_audience_size || '');
      setCreative(editRequest.creative || null);
      setContentText(editRequest.content_text || '');
      setContentCta(editRequest.content_cta || '');
      setNote(editRequest.note || '');
      setReminderEmail(editRequest.reminder_email || '');
      setCohortInputType(editRequest.cohort_input_type || 'existing');
      setManualCohortText(editRequest.manual_cohort_text || '');
    }
  }, [editRequest]);

  useEffect(() => {
    loadData();
  }, [user, selectedBrand]);

  useEffect(() => {
    loadCalendarBookings();
  }, [currentMonth]);

  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedDate]);

  const loadCalendarBookings = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const res = await getCalendarBookingsApi(startDate, endDate);
      setCalendarBookings(res.data);
    } catch (error) {
      console.error('Failed to load calendar bookings:', error);
    }
  };

  const loadData = async () => {
    try {
      const [brandsRes, slotsRes, cohortsRes] = await Promise.all([
        getBrandsApi(),
        getSlotsApi(),
        getCohortsApi()  // Get ALL cohorts, not filtered by brand
      ]);
      
      setBrands(brandsRes.data);
      
      // For viewers, use selectedBrand; for brand users, use their brand_id
      if (isViewer && selectedBrand) {
        const viewerBrand = brandsRes.data.find(b => b.id === selectedBrand);
        setBrand(viewerBrand);
      } else if (!isViewer) {
        const userBrand = brandsRes.data.find(b => b.id === user.brand_id);
        setBrand(userBrand);
      }
      
      setSlots(slotsRes.data);
      setCohorts(cohortsRes.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    try {
      const res = await getAvailableSlotsApi(selectedDate);
      setAvailableSlots(res.data);
    } catch (error) {
      console.error('Failed to load available slots:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return;
    
    const dateStr = date.toISOString().split('T')[0];
    // Toggle: if clicking the same date, deselect it
    setSelectedDate(selectedDate === dateStr ? '' : dateStr);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        showToast('Please upload a CSV file', 'error');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate ONLY mandatory fields: Campaign Name, Slot, Date
    if (!campaignName || campaignName.trim() === '') {
      showToast('Campaign name is required', 'error');
      return;
    }
    
    if (!selectedSlot) {
      showToast('Please select a slot', 'error');
      return;
    }
    
    if (!selectedDate) {
      showToast('Please select a date', 'error');
      return;
    }

    // Reminder email: required + must be @wellversed.in
    const rem = (reminderEmail || '').trim().toLowerCase();
    if (!rem) {
      setReminderEmailError('Reminder email is required');
      showToast('Reminder email is required', 'error');
      return;
    }
    if (!/^[a-z0-9._%+-]+@wellversed\.in$/i.test(rem)) {
      setReminderEmailError('Must be a valid @wellversed.in address');
      showToast('Reminder email must be a valid @wellversed.in address', 'error');
      return;
    }
    setReminderEmailError('');

    setSubmitting(true);
    try {
      const requestData = {
        campaign_name: campaignName,
        slot_id: selectedSlot,
        date: selectedDate,
        creative: abTestEnabled ? null : creative,
        drive_link: abTestEnabled ? null : (driveLink || null),
        content_text: abTestEnabled ? null : contentText,
        content_cta: abTestEnabled ? null : contentCta,
        note,
        reminder_email: rem,
        cohort_definition: cohortDefinition,
        expected_audience_size: expectedAudienceSize ? parseInt(expectedAudienceSize) : null,
        cohort_input_type: cohortInputType,
        ab_test_enabled: abTestEnabled,
        ...(abTestEnabled && {
          audience_split: audienceSplit,
          variation_1: variation1,
          variation_2: variation2
        })
      };

      // Add cohort based on type
      if (cohortInputType === 'existing' && selectedCohort) {
        requestData.cohort_id = selectedCohort;
      } else if (cohortInputType === 'csv' && csvFile) {
        requestData.cohort_csv = csvFile;
      }

      // If editing, update the request
      if (editRequest) {
        await updateRequestApi(editRequest.id, requestData);
        showToast(
          editRequest.status === 'Approved' 
            ? 'Campaign updated and sent for re-approval!' 
            : 'Campaign request updated successfully!', 
          'success'
        );
        // Navigate back to My Requests
        if (onNavigate) onNavigate('my-requests');
      } else {
        // Otherwise, create a new request
        // Viewers don't have a fixed brand_id — fall back to selectedBrand
        const requestBrandId = user.brand_id || selectedBrand;
        if (!requestBrandId) {
          showToast('Please select a brand first', 'error');
          setSubmitting(false);
          return;
        }
        await createRequestApi(requestData, user.id, requestBrandId);
        showToast('Campaign request submitted successfully!', 'success');
        
        // Reset form
        setCampaignName('');
        setSelectedSlot(null);
        setSelectedDate('');
        setSelectedCohort(null);
        setCohortDefinition('');
        setExpectedAudienceSize('');
        setCreative(null);
        setContentText('');
        setContentCta('');
        setNote('');
        setReminderEmail('');
        setReminderEmailError('');
        setCsvFile(null);
        setManualCohortText('');
        setCohortInputType('existing');
      }
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to submit request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white dark:text-white light:text-slate-900">
              {editRequest ? '✏️ Edit Campaign Request' : 'Request a Campaign'}
            </h1>
            {editRequest && editRequest.status === 'Approved' && (
              <p className="text-sm text-yellow-400 dark:text-yellow-400 light:text-yellow-600 mt-1">
                ⚠️ Editing an approved request will reset status to Pending
              </p>
            )}
          </div>
          {brand && (
            <div 
              className="px-4 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
              style={{ 
                backgroundColor: brand.color,
                color: getContrastColor(brand.color)
              }}
            >
              {brand.name}
            </div>
          )}
        </div>
        <p className="text-slate-400 dark:text-slate-400 light:text-slate-600">
          {editRequest ? 'Update your campaign request details' : 'Submit a WhatsApp campaign slot request'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 0: Campaign Name */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200">
          <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
            Campaign Name <span className="text-red-400">*</span>
          </h2>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="e.g., Summer Sale 2025, New Product Launch"
            required
            className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-600 mt-2">
            Must be unique for your brand
          </p>

          {/* A/B Testing Toggle */}
          <div className="mt-4 flex items-center justify-between p-4 rounded-xl border border-slate-700 dark:border-slate-700 light:border-slate-300">
            <div>
              <h4 className="text-sm font-semibold text-white dark:text-white light:text-slate-900">
                🧪 Enable A/B Testing
              </h4>
              <p className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-600 mt-1">
                Test two variations with different creatives and content
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAbTestEnabled(!abTestEnabled)}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors`}
              style={{ backgroundColor: abTestEnabled ? '#32B4AC' : '#475569' }}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  abTestEnabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Audience Split Section - Show Both Variations Side by Side */}
          {abTestEnabled && (
            <div className="mt-4 p-6 rounded-xl bg-slate-800/50 border-2 border-teal-500/30">
              <h4 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">📊</span>
                Audience Split
              </h4>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Variation 1 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="font-semibold text-white">Variation 1</h5>
                    <div className="px-3 py-1 bg-teal-500/20 rounded-lg">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={audienceSplit.variation1}
                        onChange={(e) => {
                          const val = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                          setAudienceSplit({ variation1: val, variation2: 100 - val });
                        }}
                        className="w-16 px-2 py-1 bg-transparent text-white font-bold text-center border-0 focus:outline-none"
                      />
                      <span className="text-white font-bold">%</span>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="text-xs text-slate-400 mb-2">Preview:</p>
                    {variation1.creative && (
                      <img src={variation1.creative} alt="Variation 1" className="w-full h-24 object-cover rounded mb-2" />
                    )}
                    <p className="text-sm text-slate-300 line-clamp-2">
                      {variation1.contentText || "No content yet"}
                    </p>
                    {variation1.contentCta && (
                      <div className="mt-2 text-xs text-teal-400">CTA: {variation1.contentCta}</div>
                    )}
                  </div>
                </div>

                {/* Variation 2 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="font-semibold text-white">Variation 2</h5>
                    <div className="px-3 py-1 bg-purple-500/20 rounded-lg">
                      <span className="text-white font-bold">{audienceSplit.variation2}%</span>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="text-xs text-slate-400 mb-2">Preview:</p>
                    {variation2.creative && (
                      <img src={variation2.creative} alt="Variation 2" className="w-full h-24 object-cover rounded mb-2" />
                    )}
                    <p className="text-sm text-slate-300 line-clamp-2">
                      {variation2.contentText || "No content yet"}
                    </p>
                    {variation2.contentCta && (
                      <div className="mt-2 text-xs text-purple-400">CTA: {variation2.contentCta}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: audienceSplit.variation1 + audienceSplit.variation2 === 100 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)' }}>
                <p className="text-sm font-medium flex items-center gap-2" style={{ color: audienceSplit.variation1 + audienceSplit.variation2 === 100 ? '#10b981' : '#ef4444' }}>
                  {audienceSplit.variation1 + audienceSplit.variation2 === 100 ? (
                    <>✓ Split configured correctly</>
                  ) : (
                    <>⚠️ Total must equal 100%</>
                  )}
                </p>
              </div>
              
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setActiveVariation(1)}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${activeVariation === 1 ? 'bg-teal-500 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                >
                  ✏️ Edit Variation 1
                </button>
                <button
                  type="button"
                  onClick={() => setActiveVariation(2)}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${activeVariation === 2 ? 'bg-purple-500 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                >
                  ✏️ Edit Variation 2
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Step 1: Select Slot */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200">
          <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
            1. Select Slot
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {slots.map((slot, index) => {
              const slotAvailable = availableSlots.find(s => s.id === slot.id);
              const isBooked = slotAvailable && !slotAvailable.is_available;
              const slotNumber = index + 1;
              
              // Slot performance labels based on time (from Trends logic)
              const getSlotLabel = (time) => {
                if (time.includes('7:00 PM') && time.includes('8:00 PM')) return { text: '⭐ Top Performer', color: '#32B4AC' };
                if (time.includes('6:00 PM') && time.includes('7:00 PM')) return { text: '✨ Great Choice', color: '#32B4AC' };
                if (time.includes('8:00 PM') && time.includes('9:00 PM')) return { text: '👍 Good Option', color: '#32B4AC' };
                return null;
              };
              
              const slotLabel = getSlotLabel(slot.time);
              
              return (
                <button
                  key={slot.id}
                  type="button"
                  onClick={() => {
                    if (isBooked) return;
                    // Toggle: if already selected, deselect it
                    setSelectedSlot(selectedSlot === slot.id ? null : slot.id);
                  }}
                  disabled={isBooked}
                  className={`
                    p-4 rounded-xl border-2 transition-all text-left hover:shadow-lg active:scale-[0.98]
                    ${
                      selectedSlot === slot.id
                        ? 'border-indigo-500 bg-indigo-500/20'
                        : isBooked
                        ? 'border-slate-700 dark:border-slate-700 light:border-slate-200 bg-slate-700/20 opacity-50 cursor-not-allowed'
                        : 'border-slate-700 dark:border-slate-700 light:border-slate-200 hover:border-slate-600'
                    }
                  `}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: slot.bg_color }}
                    />
                    <span className="font-semibold text-white dark:text-white light:text-slate-900">
                      Slot {slotNumber}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                    {slot.time}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-500 mt-1">
                    {slot.type}
                  </p>
                  {slotLabel && (
                    <div 
                      className="mt-2 px-2 py-1 rounded-lg text-xs font-semibold inline-block"
                      style={{ backgroundColor: `${slotLabel.color}15`, color: slotLabel.color }}
                    >
                      {slotLabel.text}
                    </div>
                  )}
                  {isBooked && (
                    <p className="text-xs text-red-400 dark:text-red-400 light:text-red-600 mt-2">
                      Booked by {slotAvailable.booked_by}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
          {selectedDate && availableSlots.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-500 light:text-slate-600 mt-4">
              Loading available slots for selected date...
            </p>
          )}
        </div>

        {/* Step 2: Select Date */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200">
          <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
            2. Select Date
          </h2>
          
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-2 hover:bg-slate-700 rounded transition-colors"
            >
              <ChevronLeft className="text-white" size={20} />
            </button>
            <span className="text-white font-semibold">{monthName}</span>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-2 hover:bg-slate-700 rounded transition-colors"
            >
              <ChevronRight className="text-white" size={20} />
            </button>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-slate-400 dark:text-slate-400 light:text-slate-600 py-2">
                {day}
              </div>
            ))}
            {days.map((date, idx) => {
              if (!date) {
                return <div key={`empty-${idx}`} />;
              }
              
              const dateStr = date.toISOString().split('T')[0];
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isPast = date < today;
              const isSelected = selectedDate === dateStr;
              const dateBookings = calendarBookings[dateStr] || {};
              
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => handleDateSelect(date)}
                  disabled={isPast}
                  className={`
                    rounded-xl border-2 p-2 transition-all hover:shadow-lg
                    ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : isPast
                        ? 'border-slate-700 dark:border-slate-700 light:border-slate-200 opacity-50 cursor-not-allowed'
                        : 'border-slate-700 dark:border-slate-700 light:border-slate-200 hover:border-indigo-400'
                    }
                  `}
                >
                  {/* Date number */}
                  <div className={`text-sm font-semibold mb-1 ${
                    isPast ? 'text-slate-600' : 'text-white dark:text-white light:text-slate-900'
                  }`}>
                    {date.getDate()}
                  </div>
                  
                  {/* 4 Slot indicators */}
                  <div className="grid grid-cols-2 gap-0.5">
                    {slots.slice(0, 4).map((slot, slotIdx) => {
                      const booking = dateBookings[slot.id];
                      const isBooked = !!booking;
                      
                      return (
                        <div
                          key={slot.id}
                          className={`
                            h-5 flex items-center justify-center text-[9px] font-bold
                            ${isBooked ? 'text-white rounded-lg' : 'bg-slate-700 dark:bg-slate-700 light:bg-slate-100 text-slate-400 dark:text-slate-400 light:text-slate-500 rounded-sm'}
                          `}
                          style={isBooked ? { backgroundColor: booking.brand_color } : {}}
                          title={isBooked ? `${booking.brand_name} - ${slot.time}` : `Slot ${slotIdx + 1} - ${slot.time} - Available`}
                        >
                          {isBooked ? booking.brand_abbr : `S${slotIdx + 1}`}
                        </div>
                      );
                    })}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-3 justify-center">
            {brands.map(brand => (
              <div key={brand.id} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-md shadow-sm"
                  style={{ backgroundColor: brand.color }}
                />
                <span className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-600">
                  {brand.name} ({brand.abbr})
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step 3: Cohort */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 transition-all duration-200 hover:shadow-xl">
          <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
            3. Frequently Used Cohorts
          </h2>

          {/* Two tabs for cohort input */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-700 dark:border-slate-700 light:border-slate-200 pb-2">
            <button
              type="button"
              onClick={() => setCohortInputType('existing')}
              className={`px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 active:scale-95 ${
                cohortInputType === 'existing'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-slate-700 dark:bg-slate-700 light:bg-slate-100 text-slate-300 dark:text-slate-300 light:text-slate-700 hover:bg-slate-600'
              }`}
            >
              Select Existing
            </button>
            <button
              type="button"
              onClick={() => setCohortInputType('csv')}
              className={`px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 active:scale-95 ${
                cohortInputType === 'csv'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-slate-700 dark:bg-slate-700 light:bg-slate-100 text-slate-300 dark:text-slate-300 light:text-slate-700 hover:bg-slate-600'
              }`}
            >
              Upload CSV
            </button>
          </div>

          {/* Option 1: Select Existing Cohorts */}
          {cohortInputType === 'existing' && (
            <>
              {cohorts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-400 dark:text-slate-400 light:text-slate-600">
                    No cohorts available for your brand.
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-500 light:text-slate-400 mt-2">
                    Please contact admin to create cohorts.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cohorts.map((cohort) => {
                    const isUsed = cohort.is_used || cohort.last_used_date;
                    const usedRecently = cohort.used_in_last_month;
                    
                    return (
                      <button
                        key={cohort.id}
                        type="button"
                        onClick={() => setSelectedCohort(selectedCohort === cohort.id ? null : cohort.id)}
                        className={`
                          p-4 rounded-xl border-2 transition-all text-left relative hover:shadow-lg active:scale-[0.98]
                          ${
                            selectedCohort === cohort.id
                              ? 'border-indigo-500 bg-indigo-500/20 shadow-lg'
                              : 'border-slate-700 dark:border-slate-700 light:border-slate-200 hover:border-slate-600'
                          }
                        `}
                      >
                        {/* USED Badge with theme support */}
                        {isUsed && (
                          <div className="absolute top-2 right-2">
                            <span 
                              className="text-xs font-bold px-2 py-1 rounded"
                              style={{
                                backgroundColor: usedRecently 
                                  ? 'var(--badge-used-recent-bg)' 
                                  : 'var(--badge-used-bg)',
                                color: usedRecently 
                                  ? 'var(--badge-used-recent-text)' 
                                  : 'var(--badge-used-text)'
                              }}
                            >
                              {usedRecently ? '🔥 USED' : 'USED'}
                            </span>
                          </div>
                        )}
                        
                        <div className="pr-16">
                          <span className="font-semibold text-white dark:text-white light:text-slate-900 block mb-2">
                            {cohort.name}
                          </span>
                          
                          {/* Show brand for cross-brand visibility */}
                          {cohort.brand_id && (
                            <p className="text-xs text-indigo-400 dark:text-indigo-400 light:text-indigo-600 mb-1">
                              📌 {brands.find(b => b.id === cohort.brand_id)?.name || 'Brand'}
                            </p>
                          )}
                          
                          <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                            {formatNumber(cohort.user_count)} users
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-400 mt-1">
                            {cohort.user_type}
                          </p>
                          
                          {/* Usage count */}
                          {cohort.usage_count > 0 && (
                            <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-400 mt-2">
                              📊 Used {cohort.usage_count} time{cohort.usage_count !== 1 ? 's' : ''}
                            </p>
                          )}
                          
                          {/* Last used date */}
                          {cohort.last_used_date && (
                            <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-400 mt-1">
                              Last used: {new Date(cohort.last_used_date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Info message */}
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-300 dark:text-blue-300 light:text-blue-700">
                  💡 <strong>Note:</strong> All cohorts from all brands are visible. Brand name is shown for each cohort. 
                  Cohorts marked as "USED" have been selected in previous campaigns. You can still use them.
                </p>
              </div>
            </>
          )}

          {/* Option 2: Upload CSV */}
          {cohortInputType === 'csv' && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-700/50 dark:bg-slate-700/50 light:bg-slate-50 rounded-lg border border-slate-600 dark:border-slate-600 light:border-slate-200">
                <h4 className="text-sm font-semibold text-white dark:text-white light:text-slate-900 mb-2">
                  📋 CSV Format
                </h4>
                <p className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-600 mb-3">
                  Your CSV file must include the following columns:
                </p>
                <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-lg p-3 font-mono text-xs">
                  <div className="text-green-400 dark:text-green-400 light:text-green-700">
                    user_id, name, phone, email
                  </div>
                  <div className="text-slate-500 mt-1">
                    Example:<br/>
                    U001, John Doe, +919876543210, john@example.com<br/>
                    U002, Jane Smith, +919876543211, jane@example.com
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                  Upload Audience CSV
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:text-white hover:file:shadow-lg cursor-pointer transition-all"
                  style={{ '--file-bg': '#32B4AC', '--file-hover-bg': '#2A9A93' }}
                  onMouseEnter={(e) => e.target.querySelector('input[type=file]::file-selector-button')?.style?.setProperty('background-color', '#2A9A93')}
                  onMouseLeave={(e) => e.target.querySelector('input[type=file]::file-selector-button')?.style?.setProperty('background-color', '#32B4AC')}
                />
                {csvFile && (
                  <p className="mt-2 text-sm text-green-400 dark:text-green-400 light:text-green-700">
                    ✓ File selected: {csvFile.name}
                  </p>
                )}
              </div>

              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-xs text-yellow-300 dark:text-yellow-300 light:text-yellow-700">
                  💡 The uploaded CSV will be stored securely and available for download in the admin panel and your request details.
                </p>

        {/* Create New Cohort Section */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 transition-all duration-200 hover:shadow-xl border-2 border-dashed border-slate-600 dark:border-slate-600 light:border-slate-300">
          <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
            💾 Create New Cohort
          </h2>
          <p className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600 mb-4">
            Upload a CSV file and give it a name to create a new cohort that can be reused in future campaigns.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cohort Name Input */}
            <div>
              <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                Cohort Name
              </label>
              <input
                type="text"
                value={newCohortName}
                onChange={(e) => setNewCohortName(e.target.value)}
                placeholder="e.g., Premium Users Q1 2025"
                className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-xl text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-primary transition-all"
              />
            </div>
            
            {/* CSV Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                Upload CSV
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setNewCohortCsv(e.target.files[0])}
                className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-xl text-white dark:text-white light:text-slate-900 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:text-white hover:file:shadow-lg cursor-pointer transition-all"
                style={{ '--file-bg': '#32B4AC', '--file-hover-bg': '#2A9A93' }}
              />
              {newCohortCsv && (
                <p className="mt-2 text-sm text-green-400 dark:text-green-400 light:text-green-700">
                  ✓ {newCohortCsv.name}
                </p>
              )}
            </div>
          </div>
          
          <button
            type="button"
            onClick={async () => {
              if (!newCohortName || !newCohortCsv) {
                showToast('Please provide both cohort name and CSV file', 'error');
                return;
              }
              setCreatingCohort(true);
              // TODO: Implement cohort creation API call
              showToast('Cohort creation feature coming soon!', 'info');
              setCreatingCohort(false);
            }}
            disabled={creatingCohort || !newCohortName || !newCohortCsv}
            className="mt-4 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg active:scale-[0.98]"
          >
            {creatingCohort ? 'Creating...' : '💾 Create & Save Cohort'}
          </button>
        </div>

              </div>
            </div>
          )}
        </div>

        {/* Step 3.5: Define Cohort Details */}
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 transition-all duration-200 hover:shadow-xl">
          <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
            3.5. Define Cohort Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                Define the Cohort
              </label>
              <textarea
                value={cohortDefinition}
                onChange={(e) => setCohortDefinition(e.target.value)}
                placeholder="Describe the cohort characteristics (e.g., Users who purchased in last 30 days, age 25-40, premium subscribers)"
                rows={3}
                className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
              <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-600 mt-1">
                Provide details about your target audience
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                Expected Audience Size
              </label>
              <input
                type="number"
                value={expectedAudienceSize}
                onChange={(e) => setExpectedAudienceSize(e.target.value)}
                placeholder="e.g., 5000"
                className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-600 mt-1">
                Expected number of users in this cohort
              </p>
              {expectedAudienceSize && !isNaN(expectedAudienceSize) && expectedAudienceSize > 0 && (
                <div className="mt-3 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(50, 180, 172, 0.1)', borderLeft: '3px solid #32B4AC' }}>
                  <p className="text-sm font-medium" style={{ color: '#32B4AC' }}>
                    💰 Estimated Spends: ₹{(parseFloat(expectedAudienceSize) * 0.98).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 4: Creative & Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="space-y-6">
            <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200">
              <h2 className="text-lg font-semibold text-white dark:text-white light:text-slate-900 mb-4">
                4. Upload Creative & Content {abTestEnabled && `(${activeVariation === 1 ? 'Variation 1' : 'Variation 2'})`}
              </h2>
              
              {abTestEnabled ? (
                /* A/B Testing Mode - Show inputs for active variation */
                <div className="space-y-4">
                  <CreativeUpload
                    creative={activeVariation === 1 ? variation1.creative : variation2.creative}
                    setCreative={(val) => {
                      if (activeVariation === 1) {
                        setVariation1({ ...variation1, creative: val });
                      } else {
                        setVariation2({ ...variation2, creative: val });
                      }
                    }}
                    onError={(msg) => showToast(msg, 'error')}
                  />

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Drive Link (Optional)
                    </label>
                    <input
                      type="url"
                      value={activeVariation === 1 ? variation1.driveLink : variation2.driveLink}
                      onChange={(e) => {
                        if (activeVariation === 1) {
                          setVariation1({ ...variation1, driveLink: e.target.value });
                        } else {
                          setVariation2({ ...variation2, driveLink: e.target.value });
                        }
                      }}
                      placeholder="https://drive.google.com/..."
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-xl text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Message Text
                    </label>
                    {/* Text Formatting Buttons */}
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById(`contentTextArea-${activeVariation}`);
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentText = activeVariation === 1 ? variation1.contentText : variation2.contentText;
                          const selectedText = currentText.substring(start, end);
                          if (selectedText) {
                            const newText = currentText.substring(0, start) + `*${selectedText}*` + currentText.substring(end);
                            if (activeVariation === 1) {
                              setVariation1({ ...variation1, contentText: newText });
                            } else {
                              setVariation2({ ...variation2, contentText: newText });
                            }
                          }
                        }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5 border border-slate-600"
                        title="Make text bold"
                      >
                        <span className="font-bold">B</span>
                        <span className="text-xs text-slate-400">Bold</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById(`contentTextArea-${activeVariation}`);
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentText = activeVariation === 1 ? variation1.contentText : variation2.contentText;
                          const selectedText = currentText.substring(start, end);
                          if (selectedText) {
                            const newText = currentText.substring(0, start) + `_${selectedText}_` + currentText.substring(end);
                            if (activeVariation === 1) {
                              setVariation1({ ...variation1, contentText: newText });
                            } else {
                              setVariation2({ ...variation2, contentText: newText });
                            }
                          }
                        }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm italic rounded-lg transition-colors flex items-center gap-1.5 border border-slate-600"
                        title="Make text italic"
                      >
                        <span className="italic">I</span>
                        <span className="text-xs text-slate-400">Italic</span>
                      </button>
                      <div className="ml-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700">
                        <p className="text-xs text-slate-400">
                          Select text and click Bold or Italic to format
                        </p>
                      </div>
                    </div>
                    <textarea
                      id={`contentTextArea-${activeVariation}`}
                      value={activeVariation === 1 ? variation1.contentText : variation2.contentText}
                      onChange={(e) => {
                        if (activeVariation === 1) {
                          setVariation1({ ...variation1, contentText: e.target.value });
                        } else {
                          setVariation2({ ...variation2, contentText: e.target.value });
                        }
                      }}
                      placeholder="Enter your campaign message... Use *text* for bold, _text_ for italic"
                      rows={4}
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Call-to-Action Button Text
                    </label>
                    <input
                      type="text"
                      value={activeVariation === 1 ? variation1.contentCta : variation2.contentCta}
                      onChange={(e) => {
                        if (activeVariation === 1) {
                          setVariation1({ ...variation1, contentCta: e.target.value });
                        } else {
                          setVariation2({ ...variation2, contentCta: e.target.value });
                        }
                      }}
                      placeholder="e.g., Shop Now, Learn More"
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              ) : (
                /* Regular Mode - Single creative/content */
                <div className="space-y-4">
                  <CreativeUpload
                    creative={creative}
                    setCreative={setCreative}
                    onError={(msg) => showToast(msg, 'error')}
                  />

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Drive Link (Optional)
                    </label>
                    <input
                      type="url"
                      value={driveLink}
                      onChange={(e) => setDriveLink(e.target.value)}
                      placeholder="https://drive.google.com/..."
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-xl text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-500 light:text-slate-600 mt-1">
                      Share a Google Drive link to your creative assets
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Message Text
                    </label>
                    {/* Text Formatting Buttons */}
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById('contentTextArea');
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const selectedText = contentText.substring(start, end);
                          if (selectedText) {
                            const newText = contentText.substring(0, start) + `*${selectedText}*` + contentText.substring(end);
                            setContentText(newText);
                          }
                        }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5 border border-slate-600"
                        title="Make text bold"
                      >
                        <span className="font-bold">B</span>
                        <span className="text-xs text-slate-400">Bold</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById('contentTextArea');
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const selectedText = contentText.substring(start, end);
                          if (selectedText) {
                            const newText = contentText.substring(0, start) + `_${selectedText}_` + contentText.substring(end);
                            setContentText(newText);
                          }
                        }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm italic rounded-lg transition-colors flex items-center gap-1.5 border border-slate-600"
                        title="Make text italic"
                      >
                        <span className="italic">I</span>
                        <span className="text-xs text-slate-400">Italic</span>
                      </button>
                      <div className="ml-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700">
                        <p className="text-xs text-slate-400">
                          Select text and click Bold or Italic to format
                        </p>
                      </div>
                    </div>
                    <textarea
                      id="contentTextArea"
                      value={contentText}
                      onChange={(e) => setContentText(e.target.value)}
                      placeholder="Enter your campaign message... Use *text* for bold, _text_ for italic"
                      rows={4}
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Call-to-Action Button Text
                    </label>
                    <input
                      type="text"
                      value={contentCta}
                      onChange={(e) => setContentCta(e.target.value)}
                      placeholder="e.g., Shop Now, Learn More"
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 dark:text-slate-300 light:text-slate-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Any additional notes for the admin..."
                      rows={2}
                      className="w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="sticky top-8">
            <CampaignPreview
              creative={abTestEnabled ? (activeVariation === 1 ? variation1.creative : variation2.creative) : creative}
              contentText={abTestEnabled ? (activeVariation === 1 ? variation1.contentText : variation2.contentText) : contentText}
              contentCta={abTestEnabled ? (activeVariation === 1 ? variation1.contentCta : variation2.contentCta) : contentCta}
              brandColor={brand?.color}
              brandName={brand?.name}
            />
          </div>
        </div>

        {/* Reminder Email — required, must be @wellversed.in */}
        <div className="bg-slate-800/40 dark:bg-slate-800/40 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-xl p-5" data-testid="reminder-email-section">
          <label htmlFor="reminder-email-input" className="block text-sm font-semibold text-white dark:text-white light:text-slate-900 mb-1">
            Reminder Email <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-500 mb-3">
            We'll send missing-content reminders 4h before your slot to this address. Must be a valid <span className="font-mono text-teal-400">@wellversed.in</span> email.
          </p>
          <input
            id="reminder-email-input"
            type="email"
            required
            value={reminderEmail}
            onChange={(e) => { setReminderEmail(e.target.value); if (reminderEmailError) setReminderEmailError(''); }}
            onBlur={() => {
              const rem = (reminderEmail || '').trim().toLowerCase();
              if (!rem) return;
              if (!/^[a-z0-9._%+-]+@wellversed\.in$/i.test(rem)) {
                setReminderEmailError('Must be a valid @wellversed.in address');
              }
            }}
            placeholder="you@wellversed.in"
            className={`w-full px-4 py-3 bg-slate-900 dark:bg-slate-900 light:bg-white border rounded-lg text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 ${reminderEmailError ? 'border-red-500 focus:ring-red-500' : 'border-slate-700 dark:border-slate-700 light:border-slate-300 focus:ring-teal-500'}`}
            data-testid="reminder-email-input"
          />
          {reminderEmailError && (
            <p className="mt-1.5 text-xs text-red-400" data-testid="reminder-email-error">{reminderEmailError}</p>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          {editRequest && (
            <button
              type="button"
              onClick={() => onNavigate && onNavigate('my-requests')}
              className="px-8 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98]"
            >
              Cancel
            </button>
          )}
          
          <button
            type="submit"
            disabled={!selectedSlot || !selectedDate || submitting}
            className="px-8 py-3 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:scale-[0.98]"
            style={{ backgroundColor: '#32B4AC' }}
            onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#2A9A93')}
            onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#32B4AC')}
          >
            {submitting ? (editRequest ? 'Updating...' : 'Submitting...') : (editRequest ? 'Update Campaign Request' : 'Submit Campaign Request')}
          </button>
          
          {/* Show Resend button for rejected requests */}
          {editRequest && editRequest.status === 'Rejected' && (
            <button
              type="button"
              onClick={async () => {
                try {
                  setSubmitting(true);
                  await updateRequestApi(editRequest.id, { status: 'Pending' });
                  showToast('Request resubmitted successfully!', 'success');
                  if (onNavigate) onNavigate('my-requests');
                } catch (error) {
                  showToast(error.response?.data?.detail || 'Failed to resend request', 'error');
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:scale-[0.98]"
            >
              {submitting ? 'Resending...' : 'Resend'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default RequestCampaign;
